import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {
    IPropsValues,
    SelfAlignedBounds
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {ChildLayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/ChildLayoutModel';
import {AlignItemsType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/hvbox/AlignItemsType';
import {DirectionType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/hvbox/DirectionType';
import {SelfAlignType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/hvbox/SelfAlignType';
import {LayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/LayoutModel';
import {PositioningType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/PositioningType';
import {DnDModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDModel';
import {LoaderModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/loader/LoaderModel';
import {OpacityModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/opacity/OpacityModel';
import {PerspectiveModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/perspective/PerspectiveModel';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {RepeaterDisplayType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/RepeaterDisplayType';
import {
    DEFAULT_TILES_NUMBER_OF_COUNT,
    RepeaterModel
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/RepeaterModel';
import {RotationModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/rotation/RotationModel';
import {ScaleModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/scale/ScaleModel';
import {SkewModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/skew/SkewModel';
import {FractionalUnitsType} from '@mintdata/mint-kit/api/models/FractionalUnitsType';
import {LayoutHorizontal} from '@mintdata/mint-kit/api/models/LayoutHorizontal';
import {LayoutVertical} from '@mintdata/mint-kit/api/models/LayoutVertical';
import {Offsets} from '@mintdata/mint-kit/api/models/Offsets';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {UnitsType} from '@mintdata/mint-kit/api/models/UnitsType';
import {PropName} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import _times from 'lodash/times';
import {comparer, computed, IComputedValue} from 'mobx';
import {CSSProperties} from 'react';

interface IUnitsByType
{
    pixels:number,
    percentages:number
}

const addUnits = (units:Units, result:IUnitsByType, direction:number = 1):void =>
{
    if( units && !Type.isInstanceOf(units, Type.Error) && !Type.isInstanceOf(units, Type.Empty) )
        if( UnitsType.pixels().isEqualTo(units.unitsType) )
            result.pixels += units.value
                ? units.value.value * direction
                : 0;
        else if( UnitsType.percentages().isEqualTo(units.unitsType) )
            result.percentages += units.value
                ? units.value.value * direction
                : 0;
};

const calcPositionCss = (units:IUnitsByType) =>
{
    if( !units.percentages )
        return `${units.pixels}px`;

    if( !units.pixels )
        return `${units.percentages}%`;

    return `calc(${units.pixels}px + ${units.percentages}%)`;
};

interface IRepeaterInfo
{
    readonly repeaterCount:number;
    readonly repeaterStartIndex:number;
    readonly repeaterLimit:number;
    readonly repeatCount:number;
}

export interface IRepeatedData
{
    readonly children:Array<VisualBox>;
    readonly propsValues:IPropsValues;
    readonly layout:LayoutModel;
    readonly childLayout:ChildLayoutModel;
    readonly layoutStyle:CSSProperties;
    readonly isDraggable:boolean;
    readonly isDropTarget:boolean;
    readonly dndTarget:string;
    readonly dndGroupName:string;
    readonly visibility:ABoolean;
    readonly rotation:RotationModel;
    readonly scale:ScaleModel;
    readonly opacity:OpacityModel;
    readonly skew:SkewModel;
    readonly perspective:PerspectiveModel;
    readonly loader:LoaderModel;
    readonly dnd:DnDModel;
}

export class RepeaterData implements IRepeatedData
{
    private readonly _propsRepeatedValues:Map<PropName, IComputedValue<any>> = new Map<PropName, IComputedValue<any>>();

    public constructor(public readonly visualBox:VisualBox,
                       public readonly children:Array<VisualBox>,
                       public readonly box:Box,
                       public readonly repeater:Repeater)
    {
        this.box.componentDesc.properties.forEach(prop =>
        {
            const computedBoundValues = computed(() => prop.getBoundValues(this.repeater));
            this._propsRepeatedValues.set(
                prop.name,
                computed(() => prop.getRepeatedValue(this.repeater, computedBoundValues.get()))
            );
        });
    }

    @computed({equals: comparer.structural})
    public get propsValues():IPropsValues
    {
        const propsValues:IPropsValues = {};

        this.box.componentDesc.properties.forEach(prop =>
        {
            propsValues[prop.name] = this._propsRepeatedValues.get(prop.name).get();
        });

        return this.box.componentDesc.getPropsValues(this.repeater, propsValues);
    }

    @computed/*({equals: comparer.structural})*/
    public get layout():LayoutModel
    {
        return this.box.componentDesc.layoutProp.getRepeatedValue(this.repeater);
    }

    @computed/*({equals: comparer.structural})*/
    public get childLayout():ChildLayoutModel
    {
        return this.box.componentDesc.childLayoutProp.getRepeatedValue(this.repeater);
    }

    @computed/*({equals: comparer.structural})*/
    public get layoutStyle():CSSProperties
    {
        const layoutStyle:CSSProperties = {
            ...this._getMargin()
        };

        if( PositioningType.isParentDirected(this.layout.positioningType) && this.visualBox.parent )
        {
            const parentData:IRepeatedData = this.visualBox.parent.repeatedData.get(this.visualBox.parentRepeater.index);
            const isRowDirection:boolean = DirectionType.isRow(parentData.childLayout.hvBox.direction)
                || DirectionType.isRowReversed(parentData.childLayout.hvBox.direction);

            if( isRowDirection )
            {
                if( FractionalUnitsType.isPixels(this.layout.hvBox.mainAxisSize.unitsType) )
                {
                    layoutStyle.width = this.layout.hvBox.mainAxisSize.toCssString();
                    layoutStyle.flexGrow = 0;
                }
                else
                {
                    if( FractionalUnitsType.isPercentages(this.layout.hvBox.mainAxisSize.unitsType) )
                    {
                        layoutStyle.width = this.layout.hvBox.mainAxisSize.toCssString();
                        layoutStyle.flexGrow = 1;
                    }
                    else if( FractionalUnitsType.isFractions(this.layout.hvBox.mainAxisSize.unitsType) )
                    {
                        layoutStyle.flexBasis = 0;
                        layoutStyle.flexGrow = this.layout.hvBox.mainAxisSize.value.value;
                    }
                    else
                    {
                        layoutStyle.flexGrow = 0;
                    }

                    layoutStyle.minWidth = FractionalUnitsType.isAuto(this.layout.hvBox.mainAxisMinSize.unitsType)
                        ? 0
                        : this.layout.hvBox.mainAxisMinSize.toCssString();
                    layoutStyle.maxWidth = this.layout.hvBox.mainAxisMaxSize.toCssString();
                }

                if( UnitsType.isPixels(this.layout.hvBox.crossAxisSize.unitsType) )
                {
                    layoutStyle.height = this.layout.hvBox.crossAxisSize.toCssString();
                }
                else
                {
                    if( UnitsType.isPercentages(this.layout.hvBox.crossAxisSize.unitsType) )
                        layoutStyle.height = this.layout.hvBox.crossAxisSize.toCssString();
                    else
                        layoutStyle.height = 'auto';

                    layoutStyle.minHeight = this.layout.hvBox.crossAxisMinSize.toCssString();
                    layoutStyle.maxHeight = this.layout.hvBox.crossAxisMaxSize.toCssString();
                }
            }
            else
            {
                if( FractionalUnitsType.isPixels(this.layout.hvBox.mainAxisSize.unitsType) )
                {
                    layoutStyle.height = this.layout.hvBox.mainAxisSize.toCssString();
                    layoutStyle.flexGrow = 0;
                }
                else
                {
                    if( FractionalUnitsType.isPercentages(this.layout.hvBox.mainAxisSize.unitsType) )
                    {
                        layoutStyle.height = this.layout.hvBox.mainAxisSize.toCssString();
                        layoutStyle.flexGrow = 1;
                    }
                    else if( FractionalUnitsType.isFractions(this.layout.hvBox.mainAxisSize.unitsType) )
                    {
                        layoutStyle.flexBasis = 0;
                        layoutStyle.flexGrow = this.layout.hvBox.mainAxisSize.value.value;
                    }
                    else
                    {
                        layoutStyle.flexGrow = 0;
                    }

                    layoutStyle.minHeight = FractionalUnitsType.isAuto(this.layout.hvBox.mainAxisMinSize.unitsType)
                        ? 0
                        : this.layout.hvBox.mainAxisMinSize.toCssString();
                    layoutStyle.maxHeight = this.layout.hvBox.mainAxisMaxSize.toCssString();
                }

                if( UnitsType.isPixels(this.layout.hvBox.crossAxisSize.unitsType) )
                {
                    layoutStyle.width = this.layout.hvBox.crossAxisSize.toCssString();
                }
                else
                {
                    if( UnitsType.isPercentages(this.layout.hvBox.crossAxisSize.unitsType) )
                        layoutStyle.width = this.layout.hvBox.crossAxisSize.toCssString();
                    else
                        layoutStyle.width = 'auto';

                    layoutStyle.minWidth = this.layout.hvBox.crossAxisMinSize.toCssString();
                    layoutStyle.maxWidth = this.layout.hvBox.crossAxisMaxSize.toCssString();
                }
            }

            let isSelfAlignInherited:boolean = true;

            if( SelfAlignType.isStart(this.layout.hvBox.selfAlign) )
                layoutStyle.alignSelf = 'flex-start';
            else if( SelfAlignType.isEnd(this.layout.hvBox.selfAlign) )
                layoutStyle.alignSelf = 'flex-end';
            else if( SelfAlignType.isCenter(this.layout.hvBox.selfAlign) )
                layoutStyle.alignSelf = 'center';
            else if( SelfAlignType.isStretch(this.layout.hvBox.selfAlign) )
            {
                if( isRowDirection )
                    layoutStyle.height = 'auto';
                else
                    layoutStyle.width = 'auto';

                layoutStyle.alignSelf = 'stretch';
            }
            else
                isSelfAlignInherited = false;

            if( !isSelfAlignInherited && AlignItemsType.isStretch(parentData.childLayout.hvBox.alignItems) )
            {
                if( isRowDirection )
                    layoutStyle.height = 'auto';
                else
                    layoutStyle.width = 'auto';
            }

            layoutStyle.flexShrink = this.layout.hvBox.allowShrink.value ? 1 : 0;

            if( this.layout.hvBox.itemOrder && this.layout.hvBox.itemOrder.value != null )
                layoutStyle.order = this.layout.hvBox.itemOrder.value;
        }

        return layoutStyle;
    }

    @computed
    public get visibility():ABoolean
    {
        return this.box.componentDesc.visibilityProp.getRepeatedValue(this.repeater);
    }

    @computed({equals: comparer.structural})
    public get rotation():RotationModel
    {
        return this.box.componentDesc.rotationProp.getRepeatedValue(this.repeater);
    }

    @computed({equals: comparer.structural})
    public get scale():ScaleModel
    {
        return this.box.componentDesc.scaleProp.getRepeatedValue(this.repeater);
    }

    @computed({equals: comparer.structural})
    public get opacity():OpacityModel
    {
        return this.box.componentDesc.opacityProp.getRepeatedValue(this.repeater);
    }

    @computed({equals: comparer.structural})
    public get skew():SkewModel
    {
        return this.box.componentDesc.skewProp.getRepeatedValue(this.repeater);
    }

    @computed({equals: comparer.structural})
    public get perspective():PerspectiveModel
    {
        return this.box.componentDesc.perspectiveProp.getRepeatedValue(this.repeater);
    }

    @computed({equals: comparer.structural})
    public get loader():LoaderModel
    {
        return this.box.componentDesc.loaderProp.getRepeatedValue(this.repeater);
    }

    @computed({equals: comparer.structural})
    public get dnd():DnDModel
    {
        return this.box.componentDesc.dndProp.getRepeatedValue(this.repeater);
    }

    public get dndGroupName():string
    {
        return (
            (this.dnd.isDropTarget && this.dnd.isDropTarget.value)
            || (this.dnd.isDraggable && this.dnd.isDraggable.value)
        ) && this.dnd.groupName && this.dnd.groupName.value
            ? this.dnd.groupName.toString()
            : '';
    }

    public get isDraggable():boolean
    {
        return !!(this.dnd.isDraggable && this.dnd.isDraggable.value && this.dndGroupName);
    }

    public get isDropTarget():boolean
    {
        return !!(this.dnd.isDropTarget && this.dnd.isDropTarget.value && this.dndGroupName);
    }

    public get dndTarget():string
    {
        return !!(this.dnd.isDropTarget && this.dnd.isDropTarget.value && this.dndGroupName)
        && this.dnd.dropTargetHighlightType
        && this.dnd.dropTargetHighlightType.value
            ? this.dnd.dropTargetHighlightType.value.value
            : 'false';
    }

    private _getMargin():CSSProperties
    {
        const marginFirst:Offsets = this.layout && this.layout.marginFirst ? this.layout.marginFirst : null;
        const margin:Offsets = this.layout && this.layout.margin ? this.layout.margin : null;
        const marginLast:Offsets = this.layout && this.layout.marginLast ? this.layout.marginLast : null;

        const marginStyle:CSSProperties = {
            marginLeft: margin && margin.left ? margin.left.toCssString() : 0,
            marginTop: margin && margin.top ? margin.top.toCssString() : 0,
            marginRight: margin && margin.right ? margin.right.toCssString() : 0,
            marginBottom: margin && margin.bottom ? margin.bottom.toCssString() : 0
        };

        if( marginFirst && this.repeater.index === 0 )
        {
            if( marginFirst && marginFirst.left && !marginFirst.left.isAuto() )
                marginStyle.marginLeft = marginFirst.left.toCssString();

            if( marginFirst && marginFirst.top && !marginFirst.top.isAuto() )
                marginStyle.marginTop = marginFirst.top.toCssString();

            if( marginFirst && marginFirst.right && !marginFirst.right.isAuto() )
                marginStyle.marginRight = marginFirst.right.toCssString();

            if( marginFirst && marginFirst.bottom && !marginFirst.bottom.isAuto() )
                marginStyle.marginBottom = marginFirst.bottom.toCssString();
        }

        if( marginLast && this.repeater.index === this.visualBox.repeaters.length - 1 )
        {
            if( marginLast.left && !marginLast.left.isAuto() )
                marginStyle.marginLeft = marginLast.left.toCssString();

            if( marginLast.top && !marginLast.top.isAuto() )
                marginStyle.marginTop = marginLast.top.toCssString();

            if( marginLast.right && !marginLast.right.isAuto() )
                marginStyle.marginRight = marginLast.right.toCssString();

            if( marginLast.bottom && !marginLast.bottom.isAuto() )
                marginStyle.marginBottom = marginLast.bottom.toCssString();
        }

        return marginStyle;
    }
}

export class VisualBox
{
    public readonly box:Box;
    public readonly parent:VisualBox;
    public readonly parentRepeater:Repeater;

    public constructor(box:Box,
                       parent:VisualBox,
                       parentRepeater:Repeater)
    {
        this.box = box;
        this.parent = parent;
        this.parentRepeater = parentRepeater;
    }

    @computed({equals: comparer.structural})
    public get repeaterInfo():IRepeaterInfo
    {
        const {componentDesc} = this.box;
        const repeater:Repeater = this.parentRepeater
            ? this.parentRepeater.originalChild(
                0,
                componentDesc.repeatDirection,
                componentDesc.repeaterPathInFrame,
                componentDesc.isRepeated
            )
            : Repeater.original(
                0,
                componentDesc.repeatDirection,
                componentDesc.repeaterPathInFrame,
                componentDesc.isRepeated
            );
        const basePropsValues:IPropsValues = componentDesc.getBasePropsValues(repeater);
        const {count, offset, limit} = basePropsValues.repeater as RepeaterModel;
        const repeaterCount:number = count != null && count.value != null ? count.value : 1;
        const repeaterStartIndex:number = offset != null && offset.value != null ? offset.value - 1 : 0;
        const repeaterLimit:number = limit != null && limit.value != null ? limit.value : repeaterCount;

        return {
            repeaterCount,
            repeaterStartIndex,
            repeaterLimit,
            repeatCount: Math.max(
                Math.min(repeaterCount - repeaterStartIndex, repeaterLimit),
                0
            )
        };
    }

    @computed
    public get repeaters():Array<Repeater>
    {
        const repeaters:Array<Repeater> = [];

        const {repeaterStartIndex} = this.repeaterInfo;

        let childRepeater:Repeater = this.parentRepeater
            ? this.parentRepeater.originalChild(
                repeaterStartIndex,
                this.box.componentDesc.repeatDirection,
                this.box.componentDesc.repeaterPathInFrame,
                this.box.componentDesc.isRepeated
            )
            : Repeater.original(
                repeaterStartIndex,
                this.box.componentDesc.repeatDirection,
                this.box.componentDesc.repeaterPathInFrame,
                this.box.componentDesc.isRepeated
            );

        _times(this.repeaterInfo.repeatCount, () =>
        {
            repeaters.push(childRepeater);

            childRepeater = childRepeater.next();
        });

        return repeaters;
    }

    @computed
    public get repeatedData():Map<number, IRepeatedData>
    {
        const repeatedDataMap:Map<number, IRepeatedData> = new Map<number, IRepeatedData>();
        let repeatedData:IRepeatedData = null;

        this.repeaters.forEach((repeater:Repeater) =>
        {
            const children:Array<VisualBox> = [];

            this.box.children.forEach((child:Box) =>
            {
                children.push(new VisualBox(child, this, repeater));
            });

            repeatedData = new RepeaterData(
                this,
                children,
                this.box,
                repeater
            );

            repeatedDataMap.set(
                repeater.index,
                repeatedData
            );
        });

        return repeatedDataMap;
    }

    @computed
    public get selfAlignedBounds():Map<number, SelfAlignedBounds>
    {
        const bounds:Map<number, SelfAlignedBounds> = new Map<number, SelfAlignedBounds>();

        if( this.box.parent === null )
            return bounds.set(0, this.calcViewportBounds());

        const repeaterModel:RepeaterModel = this.box.componentDesc.repeaterProp.value;

        const columnsCount:number = repeaterModel.numberOfColumns && repeaterModel.numberOfColumns.value != null
            ? repeaterModel.numberOfColumns.value
            : DEFAULT_TILES_NUMBER_OF_COUNT;

        const horizontal:IUnitsByType = {
            pixels: 0,
            percentages: 0
        };
        const vertical:IUnitsByType = {
            pixels: 0,
            percentages: 0
        };
        let column:number = 0;
        let row:number = 0;

        const {
            layoutLeft: originalLayoutLeft,
            layoutTop: originalLayoutTop,
            layoutWidth: originalLayoutWidth,
            layoutHeight: originalLayoutHeight
        } = this.box.componentDesc.getLayoutPropsValues(
            this.parentRepeater
                ? this.parentRepeater.originalChild(
                0,
                this.box.componentDesc.repeatDirection,
                this.box.componentDesc.repeaterPathInFrame,
                this.box.componentDesc.isRepeated
                )
                : Repeater.original(
                0,
                this.box.componentDesc.repeatDirection,
                this.box.componentDesc.repeaterPathInFrame,
                this.box.componentDesc.isRepeated
                )
        );

        this.repeaters.forEach((repeater:Repeater, repeatedIndex:number) =>
        {
            let currentBounds:SelfAlignedBounds;

            if( RepeaterDisplayType.isTiles(repeaterModel.displayType) )
            {
                if( column === 0 )
                    addUnits(originalLayoutLeft as Units, horizontal);
                else
                    addUnits(repeaterModel.offsetX, horizontal);

                const left:string = calcPositionCss(horizontal);

                addUnits(originalLayoutWidth as Units, horizontal);

                if( column === 0 && row === 0 )
                    addUnits(originalLayoutTop as Units, vertical);

                const top:string = calcPositionCss(vertical);

                currentBounds = {
                    left,
                    top,
                    width: originalLayoutWidth.toCssString(),
                    height: originalLayoutHeight.toCssString()
                };

                column++;

                if( column === columnsCount )
                {
                    column = 0;
                    row++;

                    horizontal.pixels = 0;
                    horizontal.percentages = 0;

                    addUnits(repeaterModel.offsetY, vertical);
                    addUnits(originalLayoutHeight as Units, vertical);
                }
            }
            else
            {
                const {
                    layoutTop,
                    layoutLeft,
                    layoutRight,
                    layoutBottom,
                    layoutWidth,
                    layoutHeight,
                    layoutVertical,
                    layoutHorizontal
                } = this.box.componentDesc.getLayoutPropsValues(repeater);

                if( RepeaterDisplayType.isHorizontal(repeaterModel.displayType) )
                {
                    if( repeatedIndex === 0 )
                    {
                        if( LayoutHorizontal.isLeft(layoutHorizontal) ) addUnits(layoutLeft as Units, horizontal);
                        else if( LayoutHorizontal.isRight(layoutHorizontal) ) addUnits(layoutRight as Units, horizontal);
                    }
                    else
                        addUnits(repeaterModel.offsetX, horizontal);

                    currentBounds = this.calcHorizontalBounds(horizontal, repeater);

                    addUnits(layoutWidth as Units, horizontal);
                }
                else if( RepeaterDisplayType.isVertical(repeaterModel.displayType) )
                {
                    if( repeatedIndex === 0 )
                    {
                        if( LayoutVertical.isTop(layoutVertical) ) addUnits(layoutTop as Units, vertical);
                        else if( LayoutVertical.isBottom(layoutVertical) ) addUnits(layoutBottom as Units, vertical);
                    }
                    else
                        addUnits(repeaterModel.offsetY, vertical);

                    currentBounds = this.calcVerticalBounds(vertical, repeater);

                    addUnits(layoutHeight as Units, vertical);
                }
                else
                {
                    currentBounds = {
                        top: layoutTop.toCssString(),
                        left: layoutLeft.toCssString(),
                        width: layoutWidth.toCssString(),
                        height: layoutHeight.toCssString()
                    };
                }
            }

            bounds.set(repeater.index, currentBounds);
        });

        return bounds;
    }

    private calcViewportBounds():SelfAlignedBounds
    {
        const {
            layoutTop,
            layoutLeft,
            layoutWidth,
            layoutHeight
        } = this.box.componentDesc;

        return {
            top: layoutTop.toCssString(),
            left: layoutLeft.toCssString(),
            width: layoutWidth.toCssString(),
            height: layoutHeight.toCssString()
        };
    }

    private calcVerticalBounds(vertical:IUnitsByType, repeater:Repeater):SelfAlignedBounds
    {
        const {
            layoutTop,
            layoutLeft,
            layoutRight,
            layoutBottom,
            layoutWidth,
            layoutHeight,
            layoutVertical,
            layoutHorizontal
        } = this.box.componentDesc.getLayoutPropsValues(repeater);

        // const {parent} = this.box;

        const bounds:SelfAlignedBounds = {};

        if( LayoutHorizontal.isLeft(layoutHorizontal) )
        {
            bounds.left = layoutLeft.toCssString();
            bounds.width = layoutWidth.toCssString();
        }
        else if( LayoutHorizontal.isRight(layoutHorizontal) )
        {
            bounds.right = layoutRight.toCssString();
            bounds.width = layoutWidth.toCssString();
        }
        else if( LayoutHorizontal.isLeftRight(layoutHorizontal) )
        {
            bounds.left = layoutLeft.toCssString();
            bounds.right = layoutRight.toCssString();
        }
        else if( LayoutHorizontal.isCenter(layoutHorizontal) )
        {
            // bounds.left = `calc(50% - ${layoutWidth.value.value / 2}px)`;
            // const parentWidth = parent.componentDesc.layoutWidth.value.value; // TODO %
            // const leftPerc = (layoutLeft.value.value + layoutWidth.value.value / 2) / parentWidth * 100;
            // bounds.left = `calc(${leftPerc}% - ${layoutWidth.value.value / 2}px)`;

            bounds.left = `calc((100% - ${layoutWidth.toCssString()}) / ${layoutLeft.value.value + layoutRight.value.value} * ${layoutLeft.value.value})`;
            bounds.width = layoutWidth.toCssString();
        }

        if( LayoutVertical.isTop(layoutVertical) )
        {
            bounds.top = calcPositionCss(vertical);
            bounds.height = layoutHeight.toCssString();
        }
        else if( LayoutVertical.isBottom(layoutVertical) )
        {
            bounds.bottom = calcPositionCss(vertical);
            bounds.height = layoutHeight.toCssString();
        }
        else if( LayoutVertical.isTopBottom(layoutVertical) )
        {
            bounds.top = layoutTop.toCssString();
            bounds.bottom = layoutBottom.toCssString();
        }
        else if( LayoutVertical.isMiddle(layoutVertical) )
        {
            // bounds.top = `calc(50% - ${layoutHeight.value.value / 2}px)`;
            // const parentHeight = parent.componentDesc.layoutHeight.value.value; // TODO %
            // const topPerc = (layoutTop.value.value + layoutHeight.value.value / 2) / parentHeight * 100;
            // bounds.top = `calc(${topPerc}% - ${layoutHeight.value.value / 2}px)`;

            bounds.top = `calc((100% - ${layoutHeight.toCssString()}) / ${layoutTop.value.value + layoutBottom.value.value} * ${layoutTop.value.value})`;
            bounds.height = layoutHeight.toCssString();
        }

        return bounds;
    }

    private calcHorizontalBounds(horizontal:IUnitsByType, repeater:Repeater):SelfAlignedBounds
    {
        const {
            layoutTop,
            layoutLeft,
            layoutRight,
            layoutBottom,
            layoutWidth,
            layoutHeight,
            layoutVertical,
            layoutHorizontal
        } = this.box.componentDesc.getLayoutPropsValues(repeater);

        // const {parent} = this.box;

        const bounds:SelfAlignedBounds = {};

        if( LayoutHorizontal.isLeft(layoutHorizontal) )
        {
            bounds.left = calcPositionCss(horizontal);
            bounds.width = layoutWidth.toCssString();
        }
        else if( LayoutHorizontal.isRight(layoutHorizontal) )
        {
            bounds.right = calcPositionCss(horizontal);
            bounds.width = layoutWidth.toCssString();
        }
        else if( LayoutHorizontal.isLeftRight(layoutHorizontal) )
        {
            bounds.left = layoutLeft.toCssString();
            bounds.right = layoutRight.toCssString();
        }
        else if( LayoutHorizontal.isCenter(layoutHorizontal) )
        {
            // bounds.left = `calc(50% - ${layoutWidth.value.value / 2}px)`;
            // const parentWidth = parent.componentDesc.layoutWidth.value.value; // TODO %
            // const leftPerc = (layoutLeft.value.value + layoutWidth.value.value / 2) / parentWidth * 100;
            // bounds.left = `calc(${leftPerc}% - ${layoutWidth.value.value / 2}px)`;

            bounds.left = `calc((100% - ${layoutWidth.toCssString()}) / ${layoutLeft.value.value + layoutRight.value.value} * ${layoutLeft.value.value})`;
            bounds.width = layoutWidth.toCssString();
        }

        if( LayoutVertical.isTop(layoutVertical) )
        {
            bounds.top = layoutTop.toCssString();
            bounds.height = layoutHeight.toCssString();
        }
        else if( LayoutVertical.isBottom(layoutVertical) )
        {
            bounds.bottom = layoutBottom.toCssString();
            bounds.height = layoutHeight.toCssString();
        }
        else if( LayoutVertical.isTopBottom(layoutVertical) )
        {
            bounds.top = layoutTop.toCssString();
            bounds.bottom = layoutBottom.toCssString();
        }
        else if( LayoutVertical.isMiddle(layoutVertical) )
        {
            // bounds.top = `calc(50% - ${layoutHeight.value.value / 2}px)`;
            // const parentHeight = parent.componentDesc.layoutHeight.value.value; // TODO %
            // const topPerc = (layoutTop.value.value + layoutHeight.value.value / 2) / parentHeight * 100;
            // bounds.top = `calc(${topPerc}% - ${layoutHeight.value.value / 2}px)`;

            bounds.top = `calc((100% - ${layoutHeight.toCssString()}) / ${layoutTop.value.value + layoutBottom.value.value} * ${layoutTop.value.value})`;
            bounds.height = layoutHeight.toCssString();
        }

        return bounds;
    }
}
